import { useEffect, useState } from "react";

import { Icon } from "@blueprintjs/core";
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  DraggableStyle,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { GripVertical } from "lucide-react";

import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { EvaluationCriteriaItemMenu } from "@/components/pieces/evaluation-criteria-manager/EvaluationCriteriaItemMenu";
import { EvaluationCriteriaMenu } from "@/components/pieces/evaluation-criteria-manager/EvaluationCriteriaMenu";
import { IconCircle } from "@/components/pieces/IconCircle";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { EvaluationCriterionFieldsFragment } from "@/graphql";
import { EvaluationCriterionItem } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

import { useEvaluationCriteriaItemModalsContext } from "./EvaluationCriteriaItemModals";
import { LinkedScenariosIndicator } from "./LinkedScenariosIndicator";
import { RubricLinkIndicator } from "./RubricLinkIndicator";

interface EvaluationCriteriaCardRichProps {
  criterion: EvaluationCriterionFieldsFragment;
  canEdit: boolean;
  refetch?: () => void;
  entityType: "scenario" | "rubric";
  scenarioId?: string;
  rubricId?: string;
}

// Reorder helper function
const reorder = (
  list: EvaluationCriterionItem[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// Styling for draggable items
const getItemStyle = (
  isDragging: boolean,
  draggableStyle?: DraggableStyle,
) => ({
  userSelect: "none" as const,
  background: isDragging ? "#f8f9fa" : "transparent",
  ...draggableStyle,
});

interface CriterionHeaderProps {
  criterion: EvaluationCriterionFieldsFragment;
  canEdit: boolean;
  entityType: "scenario" | "rubric";
  refetch?: () => void;
  openLinkedScenarios: () => void;
}

function CriterionHeader({
  criterion,
  canEdit,
  entityType,
  refetch,
  openLinkedScenarios,
}: CriterionHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="text-lg font-semibold break-words">
          {criterion.name}
        </div>
        {criterion.isLinked && criterion.sourceRubric && (
          <RubricLinkIndicator
            rubricName={criterion.sourceRubric.name}
            rubricUuid={criterion.sourceRubric.uuid}
            itemType="criterion"
          />
        )}
        {((criterion.linkedScenariosCount ?? 0) > 0 ||
          (criterion.linkedScenariosHiddenCount ?? 0) > 0) && (
          <LinkedScenariosIndicator
            visibleCount={criterion.linkedScenariosCount ?? 0}
            hiddenCount={criterion.linkedScenariosHiddenCount ?? 0}
            itemType="criterion"
            onOpenModal={openLinkedScenarios}
          />
        )}
      </div>
      {canEdit && refetch && (
        <EvaluationCriteriaMenu
          entityType={entityType}
          criterion={criterion}
          canDelete={true}
          refetch={refetch}
        />
      )}
    </div>
  );
}

interface CriterionItemProps {
  item: EvaluationCriterionItem;
  index: number;
  canEdit: boolean;
  openEditItem: (itemId: string) => void;
  openDeleteItem: (itemId: string) => void;
  openUnlinkItem: (itemId: string) => void;
  openLinkedScenariosForItem: (itemId: string) => void;
}

function CriterionItem({
  item,
  index,
  canEdit,
  openEditItem,
  openDeleteItem,
  openUnlinkItem,
  openLinkedScenariosForItem,
}: CriterionItemProps) {
  if (canEdit) {
    return (
      <Draggable key={item.id} draggableId={item.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(
              snapshot.isDragging,
              provided.draggableProps.style,
            )}
            className={snapshot.isDragging ? "mb-3 shadow-lg" : "mb-3"}
          >
            <AccordionItemContent
              item={item}
              canEdit={canEdit}
              dragHandleProps={provided.dragHandleProps ?? undefined}
              isDragging={snapshot.isDragging}
              openEditItem={openEditItem}
              openDeleteItem={openDeleteItem}
              openUnlinkItem={openUnlinkItem}
              openLinkedScenariosForItem={openLinkedScenariosForItem}
            />
          </div>
        )}
      </Draggable>
    );
  }

  return (
    <div className="mb-3">
      <AccordionItemContent
        item={item}
        canEdit={canEdit}
        openEditItem={openEditItem}
        openDeleteItem={openDeleteItem}
        openUnlinkItem={openUnlinkItem}
        openLinkedScenariosForItem={openLinkedScenariosForItem}
      />
    </div>
  );
}

interface AccordionItemContentProps {
  item: EvaluationCriterionItem;
  canEdit: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
  openEditItem: (itemId: string) => void;
  openDeleteItem: (itemId: string) => void;
  openUnlinkItem: (itemId: string) => void;
  openLinkedScenariosForItem: (itemId: string) => void;
}

function AccordionItemContent({
  item,
  canEdit,
  dragHandleProps,
  isDragging = false,
  openEditItem,
  openDeleteItem,
  openUnlinkItem,
  openLinkedScenariosForItem,
}: AccordionItemContentProps) {
  const hasLinkedScenarios =
    (item.linkedScenariosCount ?? 0) > 0 ||
    (item.linkedScenariosHiddenCount ?? 0) > 0;
  return (
    <AccordionItem
      value={item.id}
      className="rounded-md border last:border-b-1"
    >
      <AccordionTrigger className="group relative p-3 pl-4 hover:no-underline">
        <div className="flex flex-1 items-center">
          {canEdit && dragHandleProps && (
            <div
              {...dragHandleProps}
              className={`${isDragging ? "opacity-100" : ""} border-border absolute top-3 -left-2.5 flex h-8 w-5 cursor-grab items-center justify-center rounded-sm border bg-white opacity-0 transition-all group-hover:opacity-100 hover:cursor-grabbing`}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4 text-gray-400" />
            </div>
          )}
          <div className="flex flex-1 items-center gap-3">
            <IconCircle
              icon="tick"
              backgroundColor="#D3F2E5"
              color="#0D8050"
              size={24}
              iconSize={14}
            />
            <div className="flex flex-1 items-center gap-2 text-left text-base font-normal">
              {item.name}
              {item.isLinked && item.sourceRubric && (
                <RubricLinkIndicator
                  rubricName={item.sourceRubric.name}
                  rubricUuid={item.sourceRubric.uuid}
                  itemType="item"
                />
              )}
              {hasLinkedScenarios && (
                <LinkedScenariosIndicator
                  visibleCount={item.linkedScenariosCount ?? 0}
                  hiddenCount={item.linkedScenariosHiddenCount ?? 0}
                  itemType="item"
                  onOpenModal={() => openLinkedScenariosForItem(item.id)}
                />
              )}
            </div>
          </div>
          {canEdit && (
            <div
              className="ml-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <EvaluationCriteriaItemMenu
                itemId={item.id}
                isLinked={item.isLinked ?? false}
                onEdit={openEditItem}
                onUnlink={openUnlinkItem}
                onDelete={openDeleteItem}
                insideAccordion={true}
              />
            </div>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-4 select-text">
        <div className="space-y-3 pr-4 pl-[52px]">
          {item.description && (
            <div className="max-w-none text-base text-gray-700">
              <RichContentRenderer content={item.description} />
            </div>
          )}
          {item.detailedInstructions && (
            <div className="rounded-md border border-blue-400 bg-blue-50 p-3">
              <div className="mb-1 text-sm font-medium text-blue-900">
                Scenario-Specific Context:
              </div>
              <div className="text-sm text-blue-800">
                <RichContentRenderer content={item.detailedInstructions} />
              </div>
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function EvaluationCriteriaCardRich(
  props: EvaluationCriteriaCardRichProps,
) {
  const { criterion, canEdit, refetch, entityType } = props;
  const {
    openEditItem,
    openDeleteItem,
    openUnlinkItem,
    handleReorderItems,
    openAddItem,
    openLinkedScenarios,
    openLinkedScenariosForItem,
  } = useEvaluationCriteriaItemModalsContext();

  // Track which accordion items are open
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);
  // Local state for items to prevent flash during reorder
  const [localItems, setLocalItems] = useState<EvaluationCriterionItem[]>(
    ensureNonNullArray(criterion.items),
  );

  // Update local state when criterion items change
  useEffect(() => {
    setLocalItems(ensureNonNullArray(criterion.items));
  }, [criterion.items]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const reorderedItems = reorder(
      localItems,
      result.source.index,
      result.destination.index,
    );

    // Update local state immediately for instant feedback
    setLocalItems(reorderedItems);

    // Call the reorder mutation with new order
    handleReorderItems(
      reorderedItems.map((item, index) => ({
        id: item.id,
        order: index + 1,
      })),
    );
  };

  // Helper function to render the items list
  const renderItemsList = () => {
    const itemComponents = localItems?.map((item, index) => (
      <CriterionItem
        key={item.id}
        item={item}
        index={index}
        canEdit={canEdit}
        openEditItem={openEditItem}
        openDeleteItem={openDeleteItem}
        openUnlinkItem={openUnlinkItem}
        openLinkedScenariosForItem={openLinkedScenariosForItem}
      />
    ));

    if (canEdit) {
      return (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`criterion-${criterion.id}`}>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="mt-3 w-full"
              >
                <Accordion
                  type="multiple"
                  value={openAccordions}
                  onValueChange={setOpenAccordions}
                >
                  {itemComponents}
                  {provided.placeholder}
                </Accordion>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      );
    }

    return (
      <div className="mt-3 w-full">
        <Accordion
          type="multiple"
          value={openAccordions}
          onValueChange={setOpenAccordions}
        >
          {itemComponents}
        </Accordion>
      </div>
    );
  };

  // Helper function to render empty state
  const renderEmptyState = () => {
    const emptyMessage = canEdit
      ? `Add items to define specific behaviors that will be evaluated under "${criterion.name}".`
      : "This criterion doesn't have any evaluation items defined yet.";

    return (
      <div className="mt-3 rounded-md border border-dashed border-gray-400 px-6 py-8 text-center">
        <div className="font-semibold">No items yet</div>
        <div className="mx-auto mt-2 max-w-96 text-gray-500">
          {emptyMessage}
        </div>
        {canEdit && (
          <Button
            variant="minimal"
            size="sm"
            className="mt-6"
            onClick={openAddItem}
          >
            <Icon icon="plus" />
            Add Item
          </Button>
        )}
      </div>
    );
  };

  const hasItems = localItems && localItems.length > 0;
  const itemsContent = (
    <>
      {hasItems ? renderItemsList() : renderEmptyState()}
      {hasItems && canEdit && (
        <Button
          intent="secondary"
          variant="minimal"
          size="sm"
          onClick={openAddItem}
        >
          <Icon icon="plus" />
          Add Item
        </Button>
      )}
    </>
  );

  return (
    <div className="border-border rounded-lg border bg-white px-6 py-4">
      <CriterionHeader
        criterion={criterion}
        canEdit={canEdit}
        entityType={entityType}
        refetch={refetch}
        openLinkedScenarios={openLinkedScenarios}
      />
      {itemsContent}
    </div>
  );
}
